import { Controller } from "@hotwired/stimulus"

// Google Places Autocomplete for address fields.
// Loads the Google Maps JS API on connect, attaches autocomplete to the
// street input, and auto-fills city/state/zip when a place is selected.
//
// Usage:
//   .gl-address-group{ data: { controller: 'address-autocomplete' } }
//     %input{ data: { 'address-autocomplete-target': 'street' } }
//     %input{ data: { 'address-autocomplete-target': 'city' } }
//     %select{ data: { 'address-autocomplete-target': 'state' } }
//     %input{ data: { 'address-autocomplete-target': 'zip' } }
//
// Requires GOOGLE_PLACES_API_KEY set in a meta tag or window variable.

export default class extends Controller {
  static targets = ["street", "city", "state", "zip"]

  connect() {
    this.autocomplete = null
    this.loadGoogleMaps()
  }

  disconnect() {
    // Google autocomplete doesn't have a destroy method,
    // but we can remove the pac-container it creates
    document.querySelectorAll('.pac-container').forEach(el => el.remove())
  }

  loadGoogleMaps() {
    // Already loaded
    if (window.google?.maps?.places) {
      this.initAutocomplete()
      return
    }

    // Check for API key
    const apiKey = document.querySelector('meta[name="google-places-api-key"]')?.content ||
                   window.GOOGLE_PLACES_API_KEY
    if (!apiKey) {
      // No API key — gracefully degrade, no autocomplete
      return
    }

    // Check if script is already loading
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Wait for it to load
      const interval = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(interval)
          this.initAutocomplete()
        }
      }, 200)
      return
    }

    // Load the script
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=__googleMapsLoaded`
    script.async = true
    script.defer = true

    window.__googleMapsLoaded = () => {
      delete window.__googleMapsLoaded
      this.initAutocomplete()
      // Initialize any other instances waiting
      document.dispatchEvent(new CustomEvent('google-maps-loaded'))
    }

    document.head.appendChild(script)
  }

  initAutocomplete() {
    if (!this.hasStreetTarget || !window.google?.maps?.places) return

    this.autocomplete = new google.maps.places.Autocomplete(this.streetTarget, {
      types: ['address'],
      componentRestrictions: { country: ['us', 'ca'] },
      fields: ['address_components', 'formatted_address']
    })

    this.autocomplete.addListener('place_changed', () => this.onPlaceSelected())

    // Prevent form submit on Enter when autocomplete is open
    this.streetTarget.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const pacContainer = document.querySelector('.pac-container')
        if (pacContainer && pacContainer.style.display !== 'none') {
          e.preventDefault()
        }
      }
    })
  }

  onPlaceSelected() {
    const place = this.autocomplete.getPlace()
    if (!place?.address_components) return

    let street_number = ''
    let route = ''
    let city = ''
    let state = ''
    let zip = ''

    for (const component of place.address_components) {
      const type = component.types[0]
      switch (type) {
        case 'street_number':
          street_number = component.long_name
          break
        case 'route':
          route = component.long_name
          break
        case 'locality':
        case 'sublocality_level_1':
          city = component.long_name
          break
        case 'administrative_area_level_1':
          state = component.short_name
          break
        case 'postal_code':
          zip = component.long_name
          break
      }
    }

    // Fill the fields
    if (this.hasStreetTarget) {
      this.streetTarget.value = [street_number, route].filter(Boolean).join(' ')
    }
    if (this.hasCityTarget) {
      this.cityTarget.value = city
    }
    if (this.hasStateTarget) {
      // State is a <select>, find the matching option
      const option = Array.from(this.stateTarget.options).find(o => o.value === state)
      if (option) this.stateTarget.value = state
    }
    if (this.hasZipTarget) {
      this.zipTarget.value = zip
    }

    // Programmatic .value assignment fires no native event, so any gate
    // listening for input/change (e.g. business-info-gate on Step 7) wouldn't
    // see a Places selection. Dispatch a bubbling change on each filled field.
    const filled = []
    if (this.hasStreetTarget) filled.push(this.streetTarget)
    if (this.hasCityTarget) filled.push(this.cityTarget)
    if (this.hasStateTarget) filled.push(this.stateTarget)
    if (this.hasZipTarget) filled.push(this.zipTarget)
    filled.forEach((field) => field.dispatchEvent(new Event("change", { bubbles: true })))
  }
}
